import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, Check, X, AlertTriangle, RefreshCw, Copy, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import api, { mensajeDeError } from '../lib/api';
import { toast } from 'sonner';

/**
 * Estado de la pasarela de pagos.
 *
 * Le pregunta a Mercado Pago quién es el dueño del token configurado. Así se
 * sabe si las credenciales sirven antes de que un paciente intente pagar, en
 * vez de descubrirlo con una venta perdida.
 */
export const AdminPagos = () => {
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [copiado, setCopiado] = useState(false);

  const revisar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get('/admin/pagos/diagnostico');
      setEstado(data);
    } catch (error) {
      toast.error(mensajeDeError(error, 'No pudimos revisar la pasarela'));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    revisar();
  }, [revisar]);

  const copiarWebhook = async () => {
    try {
      await navigator.clipboard.writeText(estado.webhook_url);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const conectado = estado?.connection === 'ok';
  const simulado = estado?.driver === 'mock';

  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Cobros
            </CardTitle>
            <CardDescription className="text-slate-400">
              Estado de la pasarela y datos para configurarla.
            </CardDescription>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={revisar}
            disabled={cargando}
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${cargando ? 'animate-spin' : ''}`} />
            Revisar
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {cargando && !estado && <p className="text-slate-400 py-4">Consultando…</p>}

        {estado && (
          <>
            {/* Estado general */}
            <div
              className={`p-4 rounded-lg border ${
                simulado
                  ? 'bg-red-500/10 border-red-500/30'
                  : conectado
                    ? 'bg-green-500/10 border-green-500/30'
                    : 'bg-amber-500/10 border-amber-500/30'
              }`}
            >
              <div className="flex items-start gap-3">
                {simulado ? (
                  <AlertTriangle className="w-5 h-5 text-red-400 flex-none mt-0.5" />
                ) : conectado ? (
                  <Check className="w-5 h-5 text-green-400 flex-none mt-0.5" />
                ) : (
                  <X className="w-5 h-5 text-amber-400 flex-none mt-0.5" />
                )}

                <div>
                  <p
                    className={`font-medium ${
                      simulado ? 'text-red-200' : conectado ? 'text-green-200' : 'text-amber-200'
                    }`}
                  >
                    {simulado
                      ? 'Los pagos están simulados: no se cobra nada'
                      : conectado
                        ? `Conectado a Mercado Pago${estado.account?.nickname ? ` como ${estado.account.nickname}` : ''}`
                        : 'Mercado Pago no aceptó las credenciales'}
                  </p>

                  {estado.error && (
                    <p className="text-sm text-amber-200/80 mt-1">{estado.error}</p>
                  )}

                  {estado.token_mode && (
                    <p className="text-sm text-slate-400 mt-1">
                      Credenciales de{' '}
                      <strong className="text-slate-200">
                        {estado.token_mode === 'produccion' ? 'producción' : 'prueba'}
                      </strong>
                      {estado.account?.site_id && ` · cuenta ${estado.account.site_id}`}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Advertencias */}
            {(estado.warnings?.length ?? 0) > 0 && (
              <div className="space-y-2">
                {estado.warnings.map((aviso, i) => (
                  <p key={i} className="text-sm text-amber-300/90 flex gap-2">
                    <AlertTriangle className="w-4 h-4 flex-none mt-0.5" />
                    {aviso}
                  </p>
                ))}
              </div>
            )}

            {/* Datos de configuración */}
            <div className="space-y-3 pt-2">
              <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-slate-900/60">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-300">
                    Dirección del webhook (pégala en Mercado Pago)
                  </p>
                  <p className="font-mono text-xs text-slate-400 truncate">{estado.webhook_url}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={copiarWebhook}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  {copiado ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                  {copiado ? 'Copiado' : 'Copiar'}
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                <Badge className="bg-slate-700 text-slate-200 border-0">
                  Clave del webhook: {estado.webhook_secret ? 'configurada' : 'sin configurar'}
                </Badge>
                <Badge className="bg-slate-700 text-slate-200 border-0">
                  Plazo para pagar: {estado.payment_window_minutes} min
                </Badge>
              </div>

              <a
                href="https://www.mercadopago.cl/developers/panel/app"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-teal-300 hover:text-teal-200"
              >
                Abrir el panel de Mercado Pago
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminPagos;
